// @wc-ignore-file
/**
 * CR-202: voice dictation state for the chat composer, on top of the browser
 * Web Speech API. Holds no copy (the control component owns it) and never logs
 * or reports transcript text: only a thrown constructor/start goes to Sentry.
 *
 * - Support is detected at mount (not at import) so tests and the dev
 *   verification fake can install a constructor before the composer mounts.
 * - `start()` creates the recognizer synchronously, inside the tap handler:
 *   iOS Safari only starts recognition from a user gesture.
 * - Chrome ends a session on silence or after about a minute; while the user
 *   has not tapped stop the session is restarted, earlier sessions' text is
 *   kept in `committed`. Recognition also ends after two consecutive restarts
 *   that heard nothing, when the page is hidden, or when the browser aborts.
 * - CR-219: Safari in continuous mode may never fire `end` on silence, so the
 *   checks above cannot be the only way out. Two timers end a dictation
 *   without the browser's help: a silence watchdog (`SILENCE_TIMEOUT_MS` since
 *   the text last changed, across restarts) and a hard cap
 *   (`MAX_DICTATION_MS`). Both take the same graceful path as Stop. A held mic
 *   is not harmless: while Safari captures the built-in mic, macOS lowers every
 *   other app's input (WebKit bug 294623), so voice notes elsewhere go quiet.
 * - Every finished or cancelled dictation goes through `release()`: handlers
 *   detached, timers cleared, `abort()` called even after a browser `end`.
 * - A fatal error or `pagehide` ends the dictation at once instead of waiting
 *   for an `end` that may never come.
 * - `recognition.stop()` is asynchronous (a last result can arrive before
 *   `end`), so `stop()` resolves on `end`, with a 1.5 s fallback.
 */
import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  type RecognitionErrorKind,
  type SpeechRecognitionHost,
  type SpeechRecognitionLike,
  type Transcript,
  appendDictatedText,
  buildTranscript,
  getSpeechRecognitionCtor,
  isSpeechRecognitionSupported,
  mapRecognitionError,
  pickRecognitionLang,
} from "../voice-dictation";

export type VoiceDictationStatus = "idle" | "listening" | "stopping";

/** Errors the UI shows; `aborted` is never reported. */
export type VoiceDictationError = Exclude<RecognitionErrorKind, "aborted">;

export interface UseVoiceDictationOptions {
  /** The best text so far (finals plus interim), on every recognition result. */
  onTranscript: (best: string) => void;
  /**
   * A started dictation finished (stop, silence, time cap, page hidden, or an
   * error) with this final text. Not called by `cancel()`.
   */
  onEnd?: (best: string) => void;
  onError: (kind: VoiceDictationError) => void;
  /**
   * BCP-47 recognition language, read when a dictation starts (CR-202 S4:
   * the composer's picker). Defaults to the browser language. An explicit
   * lang the browser rejects reports `language` instead of retrying without
   * one (that retry would silently transcribe in `<html lang>` = English).
   */
  lang?: string;
}

export interface VoiceDictation {
  isSupported: boolean;
  status: VoiceDictationStatus;
  /** Start listening; a no-op unless idle. Call it from the tap handler. */
  start: () => void;
  /**
   * Stop listening; resolves with the final text once recognition has ended.
   * Resolves "" when idle or when `cancel()`/unmount drops the dictation, so
   * callers must treat "" as "nothing to send".
   */
  stop: () => Promise<string>;
  /** Abort without waiting; late events are ignored and `onEnd` is not called. */
  cancel: () => void;
}

/** How long `stop()` waits for the browser's `end` event. */
export const STOP_FALLBACK_MS = 1500;
/** Hard cap on one dictation, restarts included (a real timer, CR-219). */
export const MAX_DICTATION_MS = 5 * 60_000;
/** A dictation whose text has not changed for this long ends by itself (CR-219). */
export const SILENCE_TIMEOUT_MS = 20_000;
/** Consecutive restarts that heard nothing before giving up. */
const MAX_SILENT_RESTARTS = 2;
const SENTRY_TAGS = { feature: "voice-dictation" };

const EMPTY_SESSION: Transcript = { finalText: "", interimText: "" };

/** Everything about the one dictation in progress; `null` when idle. */
interface Dictation {
  recognition: SpeechRecognitionLike;
  startedAt: number;
  /** Text of earlier, auto-restarted sessions. */
  committed: string;
  session: Transcript;
  sessionHeard: boolean;
  silentRestarts: number;
  /** Keep restarting on `end` (cleared by stop, page hide and every error). */
  keepListening: boolean;
  fatalError: VoiceDictationError | null;
  langRetry: "available" | "pending" | "used";
  /** The best text when the silence watchdog was last (re)armed. */
  watchdogText: string;
  silenceTimer: ReturnType<typeof setTimeout> | null;
  capTimer: ReturnType<typeof setTimeout> | null;
  pendingStop: {
    promise: Promise<string>;
    resolve: (text: string) => void;
    timer: ReturnType<typeof setTimeout>;
  } | null;
}

function speechHost(): SpeechRecognitionHost | undefined {
  return typeof window === "undefined" ? undefined : (window as unknown as SpeechRecognitionHost);
}

function bestText(dictation: Dictation): string {
  const { finalText, interimText } = dictation.session;
  return appendDictatedText(dictation.committed, finalText + interimText);
}

/** Fold the current session into `committed` (at `end`, or when giving up on it). */
function commitSession(dictation: Dictation): void {
  dictation.committed = bestText(dictation);
  dictation.session = EMPTY_SESSION;
}

function detach(recognition: SpeechRecognitionLike): void {
  recognition.onresult = null;
  recognition.onerror = null;
  recognition.onend = null;
}

/**
 * Clear every timer of a dictation. `pendingStop` itself stays set: its
 * promise still has to settle, and a second stop() must get the same one.
 */
function clearTimers(dictation: Dictation): void {
  if (dictation.silenceTimer) {
    clearTimeout(dictation.silenceTimer);
    dictation.silenceTimer = null;
  }
  if (dictation.capTimer) {
    clearTimeout(dictation.capTimer);
    dictation.capTimer = null;
  }
  if (dictation.pendingStop) {
    clearTimeout(dictation.pendingStop.timer);
  }
}

function abortQuietly(recognition: SpeechRecognitionLike): void {
  try {
    recognition.abort();
  } catch {
    // Already ended: nothing to abort.
  }
}

/** Let go of the recognizer: no more events, no timers, mic released. */
function release(dictation: Dictation): void {
  detach(dictation.recognition);
  clearTimers(dictation);
  // Abort even after `end`: harmless then, and the strongest release otherwise.
  abortQuietly(dictation.recognition);
}

export function useVoiceDictation(options: UseVoiceDictationOptions): VoiceDictation {
  const [isSupported] = useState(() => isSpeechRecognitionSupported(speechHost()));
  const [status, setStatus] = useState<VoiceDictationStatus>("idle");

  const optionsRef = useRef(options);
  useEffect(() => {
    optionsRef.current = options;
  });

  const dictationRef = useRef<Dictation | null>(null);

  /** End of the whole dictation: back to idle, report, settle a pending stop(). */
  const finish = useCallback((dictation: Dictation) => {
    if (dictationRef.current !== dictation) {
      return;
    }
    dictationRef.current = null;
    release(dictation);
    commitSession(dictation);
    const text = dictation.committed;
    setStatus("idle");
    dictation.pendingStop?.resolve(text);
    // The one place that decides it: silence after something was dictated is
    // a normal end, not an error.
    const error = dictation.fatalError === "no_speech" && text ? null : dictation.fatalError;
    if (error) {
      optionsRef.current.onError(error);
    }
    optionsRef.current.onEnd?.(text);
  }, []);

  /** Graceful end: let a last result arrive, then `end` or the fallback finishes. */
  const requestStop = useCallback(
    (dictation: Dictation): Promise<string> => {
      if (dictation.pendingStop) {
        return dictation.pendingStop.promise;
      }
      clearTimers(dictation);
      dictation.keepListening = false;
      setStatus("stopping");
      let resolve: (text: string) => void = () => undefined;
      const promise = new Promise<string>((settle) => {
        resolve = settle;
      });
      // `end` never came: give up on the browser and keep what we have.
      const timer = setTimeout(() => finish(dictation), STOP_FALLBACK_MS);
      dictation.pendingStop = { promise, resolve, timer };
      try {
        dictation.recognition.stop();
      } catch {
        // Already ended: settle now rather than waiting for the fallback.
        finish(dictation);
      }
      return promise;
    },
    [finish],
  );

  /** (Re)arm the silence watchdog; it outlives auto-restarts. */
  const armSilenceTimer = useCallback(
    (dictation: Dictation) => {
      if (dictation.silenceTimer) {
        clearTimeout(dictation.silenceTimer);
      }
      dictation.silenceTimer = setTimeout(() => {
        dictation.silenceTimer = null;
        if (dictationRef.current !== dictation) {
          return;
        }
        dictation.fatalError ??= "no_speech";
        void requestStop(dictation);
      }, SILENCE_TIMEOUT_MS);
    },
    [requestStop],
  );

  /** The hard cap, as a real timer: Safari may never fire `end`. */
  const armCapTimer = useCallback(
    (dictation: Dictation) => {
      dictation.capTimer = setTimeout(() => {
        dictation.capTimer = null;
        if (dictationRef.current === dictation) {
          void requestStop(dictation);
        }
      }, MAX_DICTATION_MS);
    },
    [requestStop],
  );

  /** (Re)start a recognition session; false when the browser throws. */
  const startSession = useCallback((dictation: Dictation, phase: "start" | "restart"): boolean => {
    dictation.session = EMPTY_SESSION;
    dictation.sessionHeard = false;
    try {
      dictation.recognition.start();
      return true;
    } catch (error) {
      // The thrown DOMException carries no transcript.
      Sentry.captureException(error, { tags: { ...SENTRY_TAGS, phase } });
      dictation.keepListening = false;
      // A failed restart after real dictation is a normal end, not an error.
      if (!dictation.committed) {
        dictation.fatalError = "unknown";
      }
      return false;
    }
  }, []);

  const handleEnd = useCallback(
    (dictation: Dictation) => {
      commitSession(dictation);
      if (!dictation.keepListening) {
        finish(dictation);
        return;
      }
      // Backstop for the cap timer, which background tabs may throttle.
      if (Date.now() - dictation.startedAt >= MAX_DICTATION_MS) {
        finish(dictation);
        return;
      }

      if (dictation.langRetry === "pending") {
        dictation.langRetry = "used";
        dictation.recognition.lang = "";
      } else if (dictation.sessionHeard) {
        dictation.silentRestarts = 0;
      } else {
        dictation.silentRestarts += 1;
        if (dictation.silentRestarts > MAX_SILENT_RESTARTS) {
          dictation.fatalError = "no_speech";
          finish(dictation);
          return;
        }
      }

      if (!startSession(dictation, "restart")) {
        finish(dictation);
      }
    },
    [finish, startSession],
  );

  const start = useCallback(() => {
    if (!isSupported || dictationRef.current) {
      return;
    }
    const Ctor = getSpeechRecognitionCtor(speechHost());
    if (!Ctor) {
      return;
    }

    let recognition: SpeechRecognitionLike;
    try {
      recognition = new Ctor();
    } catch (error) {
      Sentry.captureException(error, {
        tags: { ...SENTRY_TAGS, phase: "create" },
      });
      optionsRef.current.onError("unknown");
      return;
    }

    const explicitLang = optionsRef.current.lang;
    const lang =
      explicitLang ?? pickRecognitionLang(typeof navigator === "undefined" ? undefined : navigator);
    if (lang) {
      recognition.lang = lang;
    }
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    const dictation: Dictation = {
      recognition,
      startedAt: Date.now(),
      committed: "",
      session: EMPTY_SESSION,
      sessionHeard: false,
      silentRestarts: 0,
      keepListening: true,
      fatalError: null,
      // Only the browser-language guess may fall back to no lang at all.
      langRetry: lang && !explicitLang ? "available" : "used",
      watchdogText: "",
      silenceTimer: null,
      capTimer: null,
      pendingStop: null,
    };

    recognition.onresult = (event) => {
      if (dictationRef.current !== dictation) {
        return;
      }
      dictation.session = buildTranscript(event.results);
      const { finalText, interimText } = dictation.session;
      if ((finalText + interimText).trim()) {
        dictation.sessionHeard = true;
      }
      const text = bestText(dictation);
      if (text !== dictation.watchdogText && !dictation.pendingStop) {
        dictation.watchdogText = text;
        armSilenceTimer(dictation);
      }
      optionsRef.current.onTranscript(text);
    };
    recognition.onerror = (event) => {
      if (dictationRef.current !== dictation) {
        return;
      }
      const kind = mapRecognitionError(event.error);
      if (kind === "no_speech") {
        // `end` follows; the restart logic decides whether to keep listening.
        return;
      }
      if (kind === "language" && dictation.langRetry === "available") {
        dictation.langRetry = "pending";
        return;
      }
      dictation.keepListening = false;
      if (kind === "aborted") {
        // Our own aborts detach first, so this one came from the browser
        // (another recognizer, mic lost): end quietly with the text so far.
        return;
      }
      dictation.fatalError = kind;
      // Nothing more will be heard, and some Chrome builds skip `end` after
      // `not-allowed`: end now (aborts) rather than wait for the browser.
      finish(dictation);
    };
    recognition.onend = () => {
      if (dictationRef.current === dictation) {
        handleEnd(dictation);
      }
    };

    dictationRef.current = dictation;
    if (startSession(dictation, "start")) {
      setStatus("listening");
      armSilenceTimer(dictation);
      armCapTimer(dictation);
    } else {
      // Never started: report the error without an `onEnd`.
      dictationRef.current = null;
      detach(recognition);
      optionsRef.current.onError("unknown");
    }
  }, [armCapTimer, armSilenceTimer, finish, handleEnd, isSupported, startSession]);

  const stop = useCallback((): Promise<string> => {
    const dictation = dictationRef.current;
    return dictation ? requestStop(dictation) : Promise.resolve("");
  }, [requestStop]);

  const cancel = useCallback(() => {
    const dictation = dictationRef.current;
    if (!dictation) {
      return;
    }
    dictationRef.current = null;
    release(dictation);
    dictation.pendingStop?.resolve("");
    setStatus("idle");
  }, []);

  // The microphone must not keep streaming from a hidden or departing page.
  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }
    const stopWhenHidden = () => {
      if (document.visibilityState === "hidden") {
        void stop();
      }
    };
    // A departing page may never run the 1.5 s fallback: end at once.
    const stopOnPageHide = () => {
      const dictation = dictationRef.current;
      if (dictation) {
        finish(dictation);
      }
    };
    document.addEventListener("visibilitychange", stopWhenHidden);
    window.addEventListener("pagehide", stopOnPageHide);
    return () => {
      document.removeEventListener("visibilitychange", stopWhenHidden);
      window.removeEventListener("pagehide", stopOnPageHide);
    };
  }, [finish, stop]);

  useEffect(() => cancel, [cancel]);

  return { isSupported, status, start, stop, cancel };
}
